/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getInvitationDetails } from "lib/invitationApi";
import { getAttendees } from "lib/userApi";
import { FetchSuccess } from "lib/managers";

import { useGlobal } from "./GeneralContext";
import useEventRouting from "hooks/useEventRouting";

interface InvitationContextData {
  invitation: any;
  isLoadingInvitation: boolean;
  attendees: any[];
  isLoadingAttendees: boolean;
  refetchInvitation: () => void;
  refetchAttendees: () => void;
  updateAttendees: (data: any) => void;
  updateInvitation: (data: any) => void;
  hasFetchedAttendees: boolean;
  hasFetchedInvitation: boolean;
}

const initialData: InvitationContextData = {
  invitation: {},
  isLoadingInvitation: false,
  attendees: [],
  isLoadingAttendees: false,
  refetchInvitation: () => {},
  refetchAttendees: () => {},
  updateAttendees: () => {},
  updateInvitation: () => {},
  hasFetchedAttendees: false,
  hasFetchedInvitation: false,
};

const InvitationContext = createContext<InvitationContextData>(initialData);

const INVITATION_QUERY_KEYS = (isLoggedIn: boolean, eventId?: string) => [
  "invitation",
  eventId,
  isLoggedIn,
];
const ATTENDEES_QUERY_KEYS = (isLoggedIn: boolean, eventId?: string) => [
  "attendees",
  eventId,
  isLoggedIn,
];

export const InvitationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isLoggedIn } = useGlobal();
  const { eventId } = useEventRouting();
  const queryClient = useQueryClient();

  // Store latest values in refs to avoid closure issues in query functions
  const eventIdRef = useRef(eventId);

  useEffect(() => {
    eventIdRef.current = eventId;
  }, [eventId]);

  const isValidId = (id: any) => !!id && id !== "undefined";

  const {
    data: invitation = {},
    isLoading: isLoadingInvitation,
    refetch: internalRefetchInvitation,
    isSuccess: hasFetchedInvitation,
  } = useQuery({
    queryKey: INVITATION_QUERY_KEYS(isLoggedIn, eventId),
    queryFn: async () => {
      const id = eventIdRef.current;
      if (!isValidId(id)) return {};

      const response = await getInvitationDetails(id);
      if (response instanceof FetchSuccess) {
        return response.data;
      }
      return response;
    },
    enabled: isValidId(eventId) && isLoggedIn,
  });

  const refetchInvitation = useCallback(() => {
    if (isValidId(eventIdRef.current)) internalRefetchInvitation();
  }, [internalRefetchInvitation]);

  const updateInvitation = (data: any) => {
    queryClient.setQueryData(INVITATION_QUERY_KEYS(isLoggedIn, eventId), data);
  };

  const {
    data: attendees = [],
    isLoading: isLoadingAttendees,
    refetch: internalRefetchAttendees,
    isSuccess: hasFetchedAttendees,
  } = useQuery({
    queryKey: ATTENDEES_QUERY_KEYS(isLoggedIn, eventId),
    queryFn: async () => {
      const id = eventIdRef.current;
      if (!isValidId(id)) return [];

      const response = await getAttendees(id);
      let data: any;
      if (response instanceof FetchSuccess) {
        data = response.data;
      } else {
        data = response;
      }

      // shapeData logic
      // event_is_vms_enabled arrives top-level on this endpoint — keep it on
      // the main attendee, since the rest of the payload is discarded here.
      return [
        {
          ...data.user_details,
          event_is_vms_enabled: data.event_is_vms_enabled,
        },
        ...data.companions_details,
        ...Array.from({ length: (data.companion_number as number) || 0 }).fill(
          {},
        ),
      ];
    },
    enabled: isValidId(eventId) && isLoggedIn,
  });

  const refetchAttendees = useCallback(() => {
    if (isValidId(eventIdRef.current)) internalRefetchAttendees();
  }, [internalRefetchAttendees]);

  const updateAttendees = (data: any) => {
    queryClient.setQueryData(ATTENDEES_QUERY_KEYS(isLoggedIn, eventId), data);
  };

  const ctx = {
    invitation,
    isLoadingInvitation,
    attendees: attendees as any[],
    isLoadingAttendees,
    refetchInvitation,
    refetchAttendees,
    updateAttendees,
    updateInvitation,
    hasFetchedAttendees,
    hasFetchedInvitation,
  };

  return (
    <InvitationContext.Provider value={ctx}>
      {children}
    </InvitationContext.Provider>
  );
};

interface IRefetchOpts {
  fetchInvitation?: boolean;
  fetchAttendees?: boolean;
  hardFetch?: true;
}

export const useInvitation = (refetchOpts?: IRefetchOpts) => {
  const rest = useContext(InvitationContext);

  const {
    refetchInvitation,
    refetchAttendees,
    isLoadingAttendees,
    isLoadingInvitation,
    attendees,
    hasFetchedAttendees,
    hasFetchedInvitation,
  } = rest;

  useEffect(() => {
    const shouldRefetchAttendees =
      refetchOpts?.fetchAttendees && !attendees.length && !hasFetchedAttendees;

    const shouldRefetchInvitation =
      refetchOpts?.fetchInvitation && !hasFetchedInvitation;

    if (
      (shouldRefetchInvitation && !isLoadingInvitation) ||
      (refetchOpts?.fetchInvitation && refetchOpts?.hardFetch)
    )
      refetchInvitation();

    if (
      (shouldRefetchAttendees && !isLoadingAttendees) ||
      (refetchOpts?.fetchAttendees && refetchOpts?.hardFetch)
    )
      refetchAttendees();
  }, []);

  return rest;
};
